/**
 * A collection period: how often a post is refreshed (`frequency_seconds`) for how long
 * (`duration_seconds`), in order from publication. The colour is a swatch KEY
 * (`red.pastel`), never a hex, so the same stored value repaints when the theme flips; `null`
 * means "the frequency's own colour".
 */
import { z } from 'zod';

import {
  PERIOD_HUES,
  PERIOD_SHADES,
  type PeriodHue,
  type PeriodShade,
} from '../../../../tokens/theme';

export { PERIOD_HUES, PERIOD_SHADES, type PeriodHue, type PeriodShade };
export type SwatchKey = `${PeriodHue}.${PeriodShade}`;

export const swatchKeySchema = z
  .string()
  .regex(/^(red|orange|yellow|green|blue|purple|gray)\.(pastel|normal|dark)$/)
  .transform((value) => value as SwatchKey);

export const periodSchema = z.object({
  frequency_seconds: z.number().int(),
  duration_seconds: z.number().int(),
  color: swatchKeySchema.nullable().default(null),
});
export type Period = z.infer<typeof periodSchema>;

/** A period while it is being typed: a field is `null` while it holds no whole number. */
export interface PeriodDraft {
  frequency_seconds: number | null;
  duration_seconds: number | null;
  color: SwatchKey | null;
}

/** The api's floor on any refresh interval. */
export const MIN_FREQUENCY_SECONDS = 300;
/** The api's floor on an audio lookup's interval. */
export const MIN_AUDIO_FREQUENCY_SECONDS = 3600;

const MINUTE = 60;
const HOUR = 3600;
export const DAY_SECONDS = 86400;
const DAY = DAY_SECONDS;

/**
 * The frequency's own colour. Boundaries, inclusive as written:
 * 5–15 min red; over 15 min and under 1 h orange; 1 h to under 6 h yellow;
 * 6 h to 12 h green; over 12 h to 24 h blue; over 24 h gray.
 */
export function defaultHue(frequencySeconds: number): PeriodHue {
  if (frequencySeconds <= 15 * MINUTE) return 'red';
  if (frequencySeconds < HOUR) return 'orange';
  if (frequencySeconds < 6 * HOUR) return 'yellow';
  if (frequencySeconds <= 12 * HOUR) return 'green';
  if (frequencySeconds <= DAY) return 'blue';
  return 'gray';
}

export function periodSwatch(period: Pick<PeriodDraft, 'frequency_seconds' | 'color'>): SwatchKey {
  if (period.color !== null) return period.color;
  return period.frequency_seconds === null
    ? 'gray.normal'
    : `${defaultHue(period.frequency_seconds)}.normal`;
}

/** The fill and the text painted on it, as theme tokens (`tokens.css`). */
export function swatchVars(key: SwatchKey): { background: string; color: string } {
  const name = key.replace('.', '-');
  return { background: `var(--period-${name})`, color: `var(--period-${name}-foreground)` };
}

/** A period whose duration is not a whole number of refreshes ends mid-interval, so the
 *  hand-over to the next period comes sooner than one full refresh. */
export function transitionShortened(
  period: Pick<PeriodDraft, 'frequency_seconds' | 'duration_seconds'>,
): boolean {
  const { frequency_seconds: frequency, duration_seconds: duration } = period;
  return frequency !== null && duration !== null && frequency > 0 && duration % frequency !== 0;
}

/** "5m", "12h", "1h30m", "7d": the compact spelling the slot summary uses. */
export function compactDuration(seconds: number): string {
  if (seconds >= DAY && seconds % DAY === 0) return `${seconds / DAY}d`;
  const days = Math.floor(seconds / DAY);
  const hours = Math.floor((seconds % DAY) / HOUR);
  const minutes = Math.round((seconds % HOUR) / MINUTE);
  const parts = [days > 0 && `${days}d`, hours > 0 && `${hours}h`, minutes > 0 && `${minutes}m`];
  return parts.filter(Boolean).join('') || '0m';
}

export function totalSeconds(periods: Pick<PeriodDraft, 'duration_seconds'>[]): number {
  return periods.reduce((sum, period) => sum + (period.duration_seconds ?? 0), 0);
}

/** Where a new period starts: one step slower than the last, for as long. */
export function nextPeriod(
  periods: PeriodDraft[],
  minFrequencySeconds: number = MIN_FREQUENCY_SECONDS,
): Period {
  const last = periods.at(-1)?.frequency_seconds ?? null;
  const frequency = Math.max(
    minFrequencySeconds,
    last === null ? 15 * MINUTE : Math.min(last * 4, DAY),
  );
  return { frequency_seconds: frequency, duration_seconds: Math.max(DAY, frequency), color: null };
}

/** A period is valid when its frequency clears the floor and it runs for at least one refresh. */
export function periodValid(
  period: PeriodDraft,
  minFrequencySeconds: number = MIN_FREQUENCY_SECONDS,
): period is Period {
  const { frequency_seconds: frequency, duration_seconds: duration } = period;
  return (
    frequency !== null &&
    duration !== null &&
    frequency >= minFrequencySeconds &&
    duration >= frequency
  );
}

/** The part of one period that falls in one day row. `startSeconds` counts from the start of
 *  the collection window, not from the row. */
export interface DayPiece {
  index: number;
  startSeconds: number;
  seconds: number;
  first: boolean;
}

export interface DayRow {
  /** 1-based: the row labelled `1d`. */
  day: number;
  pieces: DayPiece[];
  usedSeconds: number;
}

/**
 * The timeline as rows of 24h. A period crossing a day boundary is split into one piece per
 * row it touches. A period with no duration yet (a field being retyped) still gets a
 * zero-length first piece, so it stays reachable while its duration is edited.
 */
export function dayRows(periods: PeriodDraft[]): DayRow[] {
  const rows: DayRow[] = [];
  const rowAt = (day: number): DayRow => {
    while (rows.length < day) rows.push({ day: rows.length + 1, pieces: [], usedSeconds: 0 });
    return rows[day - 1]!;
  };
  let cursor = 0;
  periods.forEach((period, index) => {
    const duration = Math.max(period.duration_seconds ?? 0, 0);
    if (duration === 0) {
      rowAt(Math.floor(cursor / DAY) + 1).pieces.push({
        index,
        startSeconds: cursor,
        seconds: 0,
        first: true,
      });
      return;
    }
    const end = cursor + duration;
    let at = cursor;
    while (at < end) {
      const day = Math.floor(at / DAY) + 1;
      const seconds = Math.min(end, day * DAY) - at;
      const row = rowAt(day);
      row.pieces.push({ index, startSeconds: at, seconds, first: at === cursor });
      row.usedSeconds += seconds;
      at += seconds;
    }
    cursor = end;
  });
  return rows;
}
